import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { useRef } from 'react';
import { FancyShareDto, FancySharePublicDto } from '@diritto/common/fancyShare/dtos/fancy-share.dtos';
import { ProfileLitePublicRelayDto, ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { SectionFancyShareDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { TrackerExtraInfoProps, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { isPC } from 'constants/layout';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useAuthState } from 'contexts/AuthProvider';
import { SectionFancyShareItem } from 'app-components/home/sections/items/SectionFancyShareItem';
import { SectionSlideButton } from 'app-components/home/sections/components/SectionSlideButton';
import { SectionTitle } from 'app-components/home/sections/components/SectionTitle';
import { hideScrollbar } from 'styles/common';
import { useSlide } from 'ui-hooks/useSlide';
import { useBlockProfiles } from '@/state-hooks/accounts/useBlockProfiles';

type SectionProps = {
  section: SectionFancyShareDto;
  profileLites: Record<number, ProfileLiteRelayDto>;
  fancyShares: Record<number, FancyShareDto>;
};

const Height = 378;
const VerticalGap = 8;
const HorizontalGap = 8;
export const SectionFancyShares = ({
  section,
  fancyShares,
  profileLites,
  trackerExtraInfo,
}: SectionProps & TrackerExtraInfoProps) => {
  const { tracker } = useTrackingState();
  const { includeAdult } = useAuthState();
  const slideRef = useRef<HTMLDivElement>(null);
  const blockedProfileIds = useBlockProfiles();
  const { handlePrevSlide, handleNextSlide, handleSlide, showSlideButton } = useSlide(slideRef, HorizontalGap);

  const fancyShareIds = section.fancyShareIds.filter(fancyShareId => {
    const fancyShare = fancyShares[fancyShareId];
    if (!fancyShare || fancyShare.isDeleted) {
      return false;
    }
    if (!includeAdult && fancyShare.isAdult) {
      return false;
    }
    const profile = profileLites[fancyShare.profileId];
    if (!profile || profile.isDeleted || blockedProfileIds.has(profile.id)) {
      return false;
    }
    return true;
  });

  if (fancyShareIds.length === 0) {
    return null;
  }

  return (
    <div>
      <SectionTitle section={section} moreLink={section.moreLink} trackerExtraInfo={trackerExtraInfo} />
      <StyledWrapper>
        <SectionSlideButton
          showSlideButton={showSlideButton}
          handleNextSlide={handleNextSlide}
          handlePrevSlide={handlePrevSlide}
        />
        <StyledScrollContainer ref={slideRef} onScroll={handleSlide} rowCount={section.rowCount}>
          {fancyShareIds.map((fancyShareId, index) => {
            const fancyShare = fancyShares[fancyShareId] as FancySharePublicDto;
            const profile = profileLites[fancyShare.profileId] as ProfileLitePublicRelayDto;

            return (
              <SectionFancyShareItem
                key={fancyShareId}
                fancyShare={fancyShare}
                profile={profile}
                height={Height}
                onClick={() =>
                  tracker.track(TrackingEventType.ClickItemOnHomeSection, {
                    sectionId: section.id,
                    sectionDesign: section.design,
                    listItemIndex: index,
                    trackerExtraInfo,
                  })
                }
              />
            );
          })}
        </StyledScrollContainer>
      </StyledWrapper>
    </div>
  );
};

export default SectionFancyShares;

const StyledWrapper = styled.div`
  position: relative;
`;

const StyledScrollContainer = styled.div(
  ({ rowCount = 1 }: { rowCount?: number }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    flex-flow: column wrap;
    align-content: flex-start;
    padding: 0 16px;
    overflow-y: hidden;
    overflow-x: scroll;
    ${hideScrollbar};
    gap: ${VerticalGap}px ${HorizontalGap}px;
    height: ${Height * rowCount + VerticalGap * (rowCount - 1)}px;
    @media (${isPC}) {
      overflow-x: hidden;
      padding: 0;
      margin: 0 15px;
    }
  `,
);
